import type { FunctionReturnType } from 'convex/server';

import type { api } from '@/lib/convex';
import type { ScheduledTasksCopy } from '@/i18n/scheduled-tasks-copy';
import {
  dateKeyInTimezone,
  formatLocalWallClock,
  zonedDateTimeParts,
} from './calendar-helpers';

export type ScheduleListItem = FunctionReturnType<
  typeof api.agent_schedules.lifecycle.listSchedules
>['items'][number];

export type ScheduleListStatusFilter =
  | 'active'
  | 'paused'
  | 'completed'
  | 'all';
export type ScheduleListSort = 'default' | 'nextRun' | 'updated' | 'name';
export type ScheduleListStatus = ScheduleListItem['status'];

/** Saved paused tasks must stay discoverable when opening either list surface. */
export const DEFAULT_SCHEDULE_LIST_STATUS: ScheduleListStatusFilter = 'all';

export const SCHEDULE_LIST_STATUS_FILTERS: ScheduleListStatusFilter[] = [
  'active',
  'paused',
  'completed',
  'all',
];
export const SCHEDULE_LIST_SORTS: ScheduleListSort[] = [
  'default',
  'nextRun',
  'updated',
  'name',
];

/** The statuses one filter reads; archived schedules never show here. */
export function statusesForFilter(
  filter: ScheduleListStatusFilter
): ScheduleListStatus[] {
  return filter === 'all' ? ['active', 'paused', 'completed'] : [filter];
}

export function isScheduleListStatusFilter(
  value: unknown
): value is ScheduleListStatusFilter {
  return SCHEDULE_LIST_STATUS_FILTERS.includes(
    value as ScheduleListStatusFilter
  );
}

export function isScheduleListSort(value: unknown): value is ScheduleListSort {
  return SCHEDULE_LIST_SORTS.includes(value as ScheduleListSort);
}

function compareNextRun(left: ScheduleListItem, right: ScheduleListItem) {
  // Schedules with no upcoming run sink below the ones that will run.
  const leftAt = left.nextRunAt ?? Number.POSITIVE_INFINITY;
  const rightAt = right.nextRunAt ?? Number.POSITIVE_INFINITY;
  if (leftAt !== rightAt) return leftAt - rightAt;
  return right.createdAt - left.createdAt;
}

/** Title search plus ordering over the bounded list the query returns. */
export function arrangeScheduleList(
  items: readonly ScheduleListItem[],
  options: { search: string; sort: ScheduleListSort; locale: string }
): ScheduleListItem[] {
  const needle = options.search.trim().toLocaleLowerCase(options.locale);
  const matching = needle
    ? items.filter((item) =>
        item.title.toLocaleLowerCase(options.locale).includes(needle)
      )
    : [...items];
  const collator = new Intl.Collator(options.locale, { sensitivity: 'base' });
  return matching.sort((left, right) => {
    if (options.sort === 'nextRun') return compareNextRun(left, right);
    if (options.sort === 'updated') return right.updatedAt - left.updatedAt;
    if (options.sort === 'name') {
      return collator.compare(left.title, right.title);
    }
    return right.createdAt - left.createdAt;
  });
}

function fill(template: string, values: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in values ? (values[key] ?? match) : match
  );
}

function formatTimestampTime(
  timestamp: number,
  timezone: string,
  locale: string
): string {
  return new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timezone,
  }).format(timestamp);
}

function dayOffset(timestamp: number, now: number, timezone: string) {
  const target = dateKeyInTimezone(timestamp, timezone);
  const today = dateKeyInTimezone(now, timezone);
  if (!target || !today) return null;
  const toUtc = (key: string) => Date.parse(`${key}T00:00:00Z`);
  return Math.round((toUtc(target) - toUtc(today)) / 86_400_000);
}

/** "Today at 1:30 PM", "Tomorrow at 8:00 AM", else "Oct 3 at 1:30 PM". */
export function formatNextRunLabel(
  timestamp: number | undefined,
  options: {
    now: number;
    timezone: string;
    locale: string;
    copy: ScheduledTasksCopy;
  }
): string {
  if (timestamp === undefined) return options.copy.noNextRun;
  const time = formatTimestampTime(timestamp, options.timezone, options.locale);
  const offset = dayOffset(timestamp, options.now, options.timezone);
  if (offset === 0) return fill(options.copy.todayAt, { time });
  if (offset === 1) return fill(options.copy.tomorrowAt, { time });
  const parts = zonedDateTimeParts(timestamp, options.timezone);
  const sameYear =
    parts !== null &&
    parts.year === zonedDateTimeParts(options.now, options.timezone)?.year;
  return new Intl.DateTimeFormat(options.locale, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
    hour: 'numeric',
    minute: '2-digit',
    timeZone: options.timezone,
  }).format(timestamp);
}

/** The sidebar's compact form: "Today 1:30 PM", "Tomorrow 8 AM", "Oct 3". */
export function formatNextRunShort(
  timestamp: number,
  options: { now: number; timezone: string; locale: string }
): string {
  const offset = dayOffset(timestamp, options.now, options.timezone);
  const time = formatTimestampTime(timestamp, options.timezone, options.locale);
  if (offset !== null && offset >= 0 && offset < 7) {
    const day =
      offset <= 1
        ? new Intl.RelativeTimeFormat(options.locale, {
            numeric: 'auto',
          }).format(offset, 'day')
        : new Intl.DateTimeFormat(options.locale, {
            weekday: 'short',
            timeZone: options.timezone,
          }).format(timestamp);
    return `${day.charAt(0).toLocaleUpperCase(options.locale)}${day.slice(1)} ${time}`;
  }
  return new Intl.DateTimeFormat(options.locale, {
    month: 'short',
    day: 'numeric',
    timeZone: options.timezone,
  }).format(timestamp);
}

const WEEKDAY_SET = '1,2,3,4,5';

/** How a schedule repeats, e.g. "Daily at 1:30 PM"; unusual day sets read
 * "Custom". One-off schedules show their date. */
export function formatRecurrenceLabel(
  item: Pick<ScheduleListItem, 'recurrence' | 'timezone'>,
  options: { locale: string; now: number; copy: ScheduledTasksCopy }
): string {
  const { recurrence } = item;
  const { copy, locale } = options;
  if (recurrence.kind === 'once') {
    return formatNextRunLabel(recurrence.runAt, {
      now: options.now,
      timezone: item.timezone,
      locale,
      copy,
    });
  }
  const time = formatLocalWallClock(recurrence.localTime, locale);
  if (recurrence.kind === 'hourly') {
    return fill(copy.everyHourAt, { minute: recurrence.localTime.slice(-2) });
  }
  if (recurrence.kind === 'daily') return fill(copy.dailyAt, { time });
  if (recurrence.kind === 'monthly') {
    return fill(copy.monthlyAt, {
      day: new Intl.NumberFormat(locale).format(recurrence.dayOfMonth),
      time,
    });
  }
  const weekdays = [...recurrence.weekdays].sort((a, b) => a - b);
  if (weekdays.join(',') === WEEKDAY_SET) {
    return fill(copy.weekdaysAt, { time });
  }
  if (weekdays.length === 7) return fill(copy.dailyAt, { time });
  if (weekdays.length > 3) return copy.custom;
  const formatter = new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    timeZone: 'UTC',
  });
  const days = new Intl.ListFormat(locale, {
    style: 'short',
    type: 'conjunction',
  }).format(
    weekdays.map((weekday) => formatter.format(Date.UTC(2026, 0, 4 + weekday)))
  );
  return fill(copy.weeklyAt, { days, time });
}

/** "Daily", "Weekly", "Once"… for the sidebar's second line. */
export function recurrenceKindLabel(
  recurrence: ScheduleListItem['recurrence'],
  labels: {
    once: string;
    hourly: string;
    daily: string;
    weekly: string;
    monthly: string;
  }
): string {
  return labels[recurrence.kind];
}

/** "in 3 hours" relative to now, for the detail header. */
export function formatRelativeFromNow(
  timestamp: number,
  now: number,
  locale: string
): string {
  const deltaMinutes = Math.round((timestamp - now) / 60_000);
  const formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const abs = Math.abs(deltaMinutes);
  if (abs < 60) return formatter.format(deltaMinutes, 'minute');
  const deltaHours = Math.round(deltaMinutes / 60);
  if (Math.abs(deltaHours) < 24) return formatter.format(deltaHours, 'hour');
  return formatter.format(Math.round(deltaHours / 24), 'day');
}
